import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Loader2, Wand2, Save, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_FIELDS = [
  { k: 'proveedor', l: 'Proveedor', req: true },
  { k: 'referencia_catastral', l: 'Referencia Catastral' },
  { k: 'tipo_activo_operativo', l: 'Tipo de Operación', type: 'select', options: ['CREDITO','SUBASTA','REMATE','OCUPADO','REO','COMPRA_DIRECTA'] },
  { k: 'direccion', l: 'Dirección' },
  { k: 'municipio', l: 'Municipio' },
  { k: 'provincia', l: 'Provincia' },
  { k: 'codigo_postal', l: 'Código Postal' },
  { k: 'tipo_activo', l: 'Tipo de inmueble' },
  { k: 'metros', l: 'Metros (m²)', type: 'number' },
  { k: 'anio_construccion', l: 'Año construcción', type: 'number' },
  { k: 'ocupacion', l: 'Ocupación' },
  { k: 'vivienda_habitual', l: 'Vivienda Habitual', type: 'select', options: ['NO_CONSTA','SI','NO'] },
  { k: 'precio_compra', l: 'Precio Compra (€)', type: 'number' },
  { k: 'tasacion', l: 'Tasación (€)', type: 'number' },
  { k: 'valor_salida_base', l: 'Valor Salida Estimado / Valoración Colateral (€)', type: 'number' },
  { k: 'valor_referencia', l: 'Valor Referencia (€)', type: 'number' },
  { k: 'deuda_total', l: 'Deuda Total (€)', type: 'number' },
  { k: 'deuda_reclamada', l: 'Deuda Reclamada (€)', type: 'number' },
  { k: 'cargas', l: 'Cargas (€)', type: 'number' },
  { k: 'fase_judicial', l: 'Fase Judicial (solo CRÉDITO)', type: 'select', options: ['','sin_iniciar','demanda_presentada','auto_admision','certificacion_registral','subasta_solicitada','subasta_convocada','subasta_en_curso','subasta_celebrada','auto_adjudicacion','adjudicacion','lanzamiento','oposicion','apelacion','cerrado'] },
  { k: 'fecha_ultimo_hito_judicial', l: 'Fecha último hito', type: 'date' },
  { k: 'nif_deudor', l: 'NIF Deudor' },
  { k: 'tipo_deudor', l: 'Tipo Deudor', type: 'select', options: ['','FISICA','JURIDICA'] },
  { k: 'observaciones', l: 'Observaciones' },
];

const PdfImporterPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [pdfB64, setPdfB64] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pdf-import/templates`, { withCredentials: true })
      .then(r => setTemplates((r.data.items ?? r.data) || []))
      .catch(() => {});
  }, []);

  const readFileAsBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo PDF'); return; }
    setFile(f);
    setPdfB64(await readFileAsBase64(f));
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/pdf-import/extract`, fd, { withCredentials: true, timeout: 180000 });
      const ex = r.data.extracted || {};
      // Defaults
      if (!ex.vivienda_habitual) ex.vivienda_habitual = 'NO_CONSTA';
      // Compat: el prompt antiguo devolvía `estado_procesal` con texto libre.
      // Ahora preferimos `fase_judicial` (enum). Si Gemini devolvió estado_procesal
      // y no fase_judicial, intentamos auto-mapear contra los valores canónicos.
      if (ex.estado_procesal && !ex.fase_judicial) {
        ex.fase_judicial = ex.estado_procesal;  // backend lo normaliza
      }
      // Fase judicial sólo aplica a CRÉDITO. Si Gemini la devolvió pero el
      // tipo_activo_operativo no es CREDITO, la borramos para evitar warnings.
      if (ex.fase_judicial && (ex.tipo_activo_operativo || '').toUpperCase() !== 'CREDITO') {
        ex.fase_judicial = null;
        ex.estado_procesal = null;
      }
      setForm(ex);
      if (r.data.suggested_template) {
        toast.success(`Plantilla detectada: ${r.data.suggested_template.nombre}`);
        setTemplateName(r.data.suggested_template.nombre);
      } else {
        toast.success('Datos extraídos del PDF');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error extrayendo datos');
    } finally {
      setExtracting(false);
    }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setNum = (k, v) => set(k, v === '' ? null : parseFloat(v));

  const handleCreate = async () => {
    if (!form.proveedor) { toast.error('Falta "proveedor"'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/pdf-import/create-asset`,
        { asset: form, pdf_base64: pdfB64, pdf_filename: file?.name },
        { withCredentials: true, timeout: 180000 });
      const a = r.data.asset;
      toast.success(`Activo ${a.codigo_npl} creado · ${r.data.num_imagenes_adjuntadas} imágenes adjuntadas`);
      navigate(`/activos/${a.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando activo');
    } finally {
      setCreating(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Pon nombre a la plantilla'); return; }
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/pdf-import/templates`, {
        nombre: templateName,
        proveedor_detectado: form.proveedor || '',
        mapping: {},  // por ahora informativo; el extractor ya infiere
      }, { withCredentials: true });
      toast.success('Plantilla guardada');
      const r = await axios.get(`${API}/pdf-import/templates`, { withCredentials: true });
      setTemplates((r.data.items ?? r.data) || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando plantilla');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar plantilla "${nombre}"?`)) return;
    await axios.delete(`${API}/pdf-import/templates/${id}`, { withCredentials: true });
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="pdf-importer-page">
      <div>
        <h1 className="heading-1 flex items-center gap-2"><FileText className="w-6 h-6 text-blue-600" /> Importar Ficha PDF</h1>
        <p className="body-text mt-2">Sube una ficha PDF de oportunidad (Fencia, Anticipa, etc.). El sistema extrae los campos con IA y puedes ajustarlos antes de crear el activo. Las imágenes del PDF se adjuntan automáticamente como documentos.</p>
      </div>

      {/* Upload */}
      {!file && (
        <div className="card-base p-8 border-2 border-dashed border-slate-300 text-center">
          <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="heading-3">Sube un PDF de oportunidad</h3>
          <p className="text-sm text-slate-500 mt-2 mb-4">Formato soportado: PDF (máx. 20 MB). Gemini 2.5 Flash extraerá los datos.</p>
          <input ref={fileRef} type="file" accept=".pdf" onChange={e => handleFile(e.target.files?.[0])} className="hidden" data-testid="pdf-file-input" />
          <button onClick={() => fileRef.current?.click()} className="btn-primary" data-testid="pdf-upload-btn">
            Seleccionar PDF
          </button>
        </div>
      )}

      {file && extracting && (
        <div className="card-base p-8 text-center">
          <Loader2 className="w-8 h-8 text-blue-600 mx-auto animate-spin" />
          <p className="heading-3 mt-3">Analizando {file.name} con Gemini 2.5 Flash...</p>
          <p className="text-sm text-slate-500 mt-1">Puede tardar 15-30s según el tamaño del documento.</p>
        </div>
      )}

      {file && !extracting && (
        <>
          <div className="card-base p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB · extracción completada</p>
              </div>
            </div>
            <button onClick={() => { setFile(null); setForm({}); setPdfB64(''); }} className="btn-secondary text-sm">Cambiar archivo</button>
          </div>

          <div className="card-base p-6">
            <h3 className="heading-3 flex items-center gap-2 mb-4"><Wand2 className="w-5 h-5 text-purple-600" /> Datos extraídos · revisa y ajusta</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ASSET_FIELDS.map(f => (
                <div key={f.k} className="space-y-1">
                  <label className="text-xs text-slate-600">
                    {f.l}{f.req && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {f.type === 'select' ? (
                    <select className="input-base text-sm" value={form[f.k] ?? ''} onChange={e => set(f.k, e.target.value || null)} data-testid={`pdf-f-${f.k}`}>
                      {(f.options || []).map(o => <option key={o} value={o}>{o || '—'}</option>)}
                    </select>
                  ) : f.type === 'number' ? (
                    <input type="number" className="input-base text-sm" value={form[f.k] ?? ''} onChange={e => setNum(f.k, e.target.value)} data-testid={`pdf-f-${f.k}`} />
                  ) : f.type === 'date' ? (
                    <input type="date" className="input-base text-sm" value={form[f.k] ?? ''} onChange={e => set(f.k, e.target.value || null)} data-testid={`pdf-f-${f.k}`} />
                  ) : (
                    <input className="input-base text-sm" value={form[f.k] ?? ''} onChange={e => set(f.k, e.target.value)} data-testid={`pdf-f-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="card-base p-6">
            <h3 className="heading-3 mb-3">Plantillas de importación</h3>
            <p className="text-sm text-slate-500 mb-3">Las plantillas te ayudan a recordar el mapeo para PDFs del mismo proveedor.</p>
            <div className="flex gap-2 items-stretch flex-wrap mb-4">
              <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Nombre (ej. Fencia v1)" className="input-base text-sm flex-1 min-w-[240px]" data-testid="pdf-template-name" />
              <button onClick={saveTemplate} disabled={savingTemplate || !templateName.trim()} className="btn-secondary text-sm flex items-center gap-2" data-testid="pdf-template-save">
                {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar plantilla
              </button>
            </div>
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <div key={t.id} className="inline-flex items-center gap-2 bg-slate-50 rounded-md pl-3 pr-1 py-1 border border-slate-200">
                    <span className="text-sm font-medium">{t.nombre}</span>
                    {t.proveedor_detectado && <span className="text-xs text-slate-500">· {t.proveedor_detectado}</span>}
                    <button onClick={() => deleteTemplate(t.id, t.nombre)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`pdf-template-del-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Las imágenes del PDF (≥200×200 px) se adjuntarán automáticamente como documentos del activo.</p>
            <button onClick={handleCreate} disabled={creating || !form.proveedor} className="btn-primary" data-testid="pdf-create-btn">
              {creating ? (<><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Creando activo...</>) : 'Crear activo desde PDF'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PdfImporterPage;
